import React from "react";
import { useNavigate } from "react-router-dom";
import { Clapperboard } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import Button from "../../../../components/buttons/Button";

import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";

import { useCreatorChannelVideos } from "../hooks/useCreatorChannelVideos";

import "./ChannelVideosView.css";

const VIDEOS_LIMIT = 12;

const isVideoProcessing = (video) => {
  const status = String(video?.hlsStatus || "")
    .trim()
    .toLowerCase();

  return ["pending", "processing"].includes(status);
};

export default function ChannelVideosView({ username }) {
  const navigate = useNavigate();

  const { videos, loading, loadingMore, error, hasMore, refresh, loadMore } =
    useCreatorChannelVideos({
      username,
      limit: VIDEOS_LIMIT,
    });

  const videoItems = Array.isArray(videos) ? videos : [];
  const hasVideos = videoItems.length > 0;
  const isBusy = loading || loadingMore;

  const handleOpenVideo = (video) => {
    if (!video?.videoTag || isVideoProcessing(video)) return;

    navigate(`/watch/${encodeURIComponent(video.videoTag)}`);
  };

  const renderVideo = (video) => (
    <VideoThumbnailCard
      key={video.videoId ?? video.videoTag}
      video={video}
      disabled={isVideoProcessing(video)}
      onClick={handleOpenVideo}
      showPlayButton
    />
  );

  return (
    <ViewContainer>
      <ViewHeader title="Videos" description="Explore videos and content." />

      {error && hasVideos && (
        <div className="channel-videos__alert" role="alert">
          <StatusAlert type="error" message={error} />
        </div>
      )}

      {loading && !hasVideos ? (
        <ContentGrid
          items={[]}
          loading
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={VIDEOS_LIMIT}
        />
      ) : !hasVideos ? (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title={error ? "Unable to load videos" : "No videos available"}
          description={error || "No videos have been shared on this channel."}
          action={
            error ? (
              <Button
                value="Try Again"
                variant="primary-outlined"
                onClick={refresh}
                disabled={isBusy}
              />
            ) : null
          }
        />
      ) : (
        <>
          <ContentGrid
            items={videoItems}
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            showEmpty={false}
            renderItem={renderVideo}
          />

          {hasMore && (
            <div className="channel-videos__footer">
              <Button
                value={loadingMore ? "Loading..." : "Load More"}
                variant="primary-outlined"
                disabled={isBusy}
                onClick={loadMore}
              />
            </div>
          )}
        </>
      )}
    </ViewContainer>
  );
}
